import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Save, Mail, Lock, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const TRIGGER_LABELS = {
  "application_received": "Application received",
  "status_changed:Under Review": "Status → Under Review",
  "status_changed:Reviewed": "Status → Reviewed",
  "status_changed:Interview": "Status → Interview",
  "status_changed:Further Assessment": "Status → Further Assessment",
  "status_changed:Accepted": "Status → Accepted",
  "status_changed:Rejected": "Status → Rejected",
};

const inputCls = "w-full border border-[#0F0F0F] bg-transparent px-3 py-2";

export default function EmailTemplatesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("status_changed:Interview");
  const [form, setForm] = useState({ subject: "", body_html: "", is_enabled: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/recruiter/email-templates");
      setData(data);
      const existing = (data.templates || []).find((t) => t.trigger === active);
      setForm(existing ? { subject: existing.subject || "", body_html: existing.body_html || "", is_enabled: !!existing.is_enabled }
                       : { subject: "", body_html: "", is_enabled: true });
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!data) return;
    const existing = (data.templates || []).find((t) => t.trigger === active);
    setForm(existing ? { subject: existing.subject || "", body_html: existing.body_html || "", is_enabled: !!existing.is_enabled }
                     : { subject: "", body_html: "", is_enabled: true });
    // Clear error on trigger change only — leave success briefly so users see save confirmation
    setError("");
  }, [active]);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true); setError(""); setSuccess("");
    try {
      await api.put(`/recruiter/email-templates/${encodeURIComponent(active)}`, form);
      setSuccess("Template saved.");
      // Refresh just the list, without re-running the trigger-effect's reset
      const { data: fresh } = await api.get("/recruiter/email-templates");
      setData(fresh);
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove this template? Status updates will revert to no automatic email.")) return;
    try {
      await api.delete(`/recruiter/email-templates/${encodeURIComponent(active)}`);
      setSuccess("Template removed.");
      await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  if (loading) return <div className="mt-6 text-[#5C5C5C] text-sm">Loading…</div>;
  if (!data) return null;

  if (!data.is_growth_plus) {
    return (
      <div className="mt-6 max-w-2xl border border-[#0F0F0F] p-6" data-testid="templates-paywall">
        <div className="label-eyebrow flex items-center gap-1.5"><Lock className="w-3 h-3" /> Growth feature</div>
        <h2 className="font-display font-black text-2xl mt-2">Custom email templates.</h2>
        <p className="text-sm text-[#5C5C5C] mt-2">
          Write your own messages for application updates — friendly rejections, interview invites, offer letters.
          Available on the <strong>Growth</strong> and <strong>Enterprise</strong> plans.
        </p>
        <Link to="/billing" data-testid="templates-upgrade-cta" className="btn-terra mt-4 inline-flex">
          Upgrade to Growth
        </Link>
      </div>
    );
  }

  const triggers = data.available_triggers || [];
  const configured = new Set((data.templates || []).map((t) => t.trigger));
  const tokens = ["candidate_name", "candidate_email", "job_title", "company_name", "status"];

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6" data-testid="templates-editor">
      <aside className="border border-[#E2E0D8] p-3 space-y-1">
        <div className="label-eyebrow flex items-center gap-1.5"><Mail className="w-3 h-3" /> Triggers</div>
        {triggers.map((t) => (
          <button
            key={t}
            data-testid={`template-trigger-${t}`}
            onClick={() => setActive(t)}
            className={`w-full text-left text-sm px-3 py-2 flex items-center justify-between ${active === t ? "bg-[#0F0F0F] text-[#FDFCF8]" : "hover:bg-[#E2E0D8]/40"}`}
          >
            <span className="truncate">{TRIGGER_LABELS[t] || t}</span>
            {configured.has(t) && <span className="text-[10px] uppercase font-semibold tracking-widest">ON</span>}
          </button>
        ))}
      </aside>

      <form onSubmit={save} className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="label-eyebrow">Editing</div>
            <h2 className="font-display font-black text-2xl mt-0.5">{TRIGGER_LABELS[active] || active}</h2>
          </div>
          <label className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold">
            <input
              data-testid="template-enabled"
              type="checkbox"
              checked={form.is_enabled}
              onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
            />
            Enabled
          </label>
        </div>

        <div>
          <label className="label-eyebrow block mb-1.5">Subject</label>
          <input data-testid="template-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="You're shortlisted for {{job_title}}" className={inputCls} />
        </div>

        <div>
          <label className="label-eyebrow block mb-1.5">Body (HTML allowed)</label>
          <textarea data-testid="template-body" rows={10} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} placeholder="<p>Hi {{candidate_name}},</p><p>We'd love to invite you to a quick chat about the {{job_title}} role.</p>" className={`${inputCls} font-mono text-xs`} />
        </div>

        <div className="text-xs text-[#5C5C5C]">
          <span className="font-semibold">Variables: </span>
          {tokens.map((t) => (
            <code key={t} className="ml-2 px-1.5 py-0.5 bg-[#E2E0D8] text-[#0F0F0F]">{`{{${t}}}`}</code>
          ))}
        </div>

        {error && <div data-testid="template-error" className="text-sm text-[var(--brand-primary)]">{error}</div>}
        {success && <div data-testid="template-success" className="text-sm text-green-700">{success}</div>}

        <div className="flex items-center gap-2">
          <button type="submit" disabled={saving || !form.subject.trim() || !form.body_html.trim()} data-testid="template-save" className="btn-terra disabled:opacity-40">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save template"}
          </button>
          {configured.has(active) && (
            <button type="button" onClick={remove} data-testid="template-delete" className="btn-ghost text-sm text-[var(--brand-primary)]">
              <Trash2 className="w-4 h-4" /> Remove
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
